type Separators = {
  decimal: '.' | ','
  thousand: ',' | '.' | ' ' | '\u00A0' | ''
}

interface ExtractFiatSeparatorsProps {
  fiatCode?: string
  preferDeviceLocale?: boolean
}

/**
 * 🌐 Get the decimal and thousand separators based on the current locale.
 * Falls back to US standard if the result is ambiguous or unsupported.
 */
export const extractFiatSeparators = ({
  fiatCode,
  preferDeviceLocale,
}: ExtractFiatSeparatorsProps): Separators => {
  const locale = preferDeviceLocale
    ? undefined
    : extractLocaleFromFiatCode(fiatCode)

  try {
    const formatted = new Intl.NumberFormat(locale).format(1234567.89)
    // Examples:
    // - en-US: "1,234,567.89"
    // - pt-BR: "1.234.567,89"
    // - de-DE: "1.234.567,89"
    // - ru-RU: "1 234 567,89" (space or non-breaking space)

    // Extract all non-numeric characters
    const matches = formatted.match(/[^0-9]/g) || []

    // Filter only valid separators for thousand: dot, comma, space (normal and non-breaking)
    const validThousandSeparators = ['.', ',', ' ', '\u00A0']

    // Filter only dots and commas for decimal, but thousand can be space as well
    const separators = matches.filter((char) =>
      validThousandSeparators.includes(char),
    )

    const decimal = separators.pop() // Last separator is typically the decimal
    const thousand = separators[0] // First separator is typically the thousand

    // Validate decimal separator must be "." or ","
    if (
      (decimal !== '.' && decimal !== ',') ||
      (thousand && !validThousandSeparators.includes(thousand))
    ) {
      return {
        decimal: '.',
        thousand: ',',
      }
    }

    return {
      decimal: (decimal as '.' | ',') || '.',
      thousand: (thousand as ',' | '.' | ' ' | '\u00A0' | '') || '',
    }
  } catch {
    return {
      decimal: '.',
      thousand: ',',
    }
  }
}

/**
 * Extract locale from fiat code (complete mapping from React Native)
 */
function extractLocaleFromFiatCode(fiatCode?: string): string | undefined {
  if (!fiatCode) return undefined

  const CURRENCY_CODE_TO_LOCALE_MAP: Record<string, string> = {
    AED: 'ar-AE',
    AUD: 'en-AU',
    BHD: 'ar-BH',
    BRL: 'pt-BR',
    CHF: 'de-CH',
    CNY: 'zh-CN',
    DZD: 'ar-DZ',
    EGP: 'ar-EG',
    EUR: 'de-DE',
    GBP: 'en-GB',
    GHS: 'en-GH',
    IDR: 'id-ID',
    INR: 'hi-IN',
    JPY: 'ja-JP',
    KES: 'en-KE',
    KRW: 'ko-KR',
    KWD: 'ar-KW',
    MAD: 'ar-MA',
    MUR: 'en-MU',
    NGN: 'en-NG',
    OMR: 'ar-OM',
    PHP: 'en-PH',
    QAR: 'ar-QA',
    RUB: 'ru-RU',
    SAR: 'ar-SA',
    SCR: 'en-SC',
    SGD: 'en-SG',
    TND: 'ar-TN',
    TRY: 'tr-TR',
    TZS: 'en-TZ',
    UGX: 'en-UG',
    USD: 'en-US',
    ZAR: 'en-ZA',
  }

  return CURRENCY_CODE_TO_LOCALE_MAP[fiatCode] || 'en-US'
}
